import Contacts from "@/components/Contacts";
import SEO from "@/components/SEO";
import {
  getMainLayoutQuery,
  MainLayoutQuery,
  settingsQuery,
  seoQuerySchema,
} from "@/lib/sanity/queries";
import { sanityClient } from "@/lib/sanity/server";
import { GetStaticProps, NextPage } from "next";
import { groq } from "next-sanity";
import useSWR from "swr";
import { z } from "zod";
import GridOutline from "@/components/GridOutline/GridOutline";
import { mergeWith } from "lodash";
import React, { useMemo } from "react";
import ContactCarousel, { LocationsType } from "@/components/ContactCarousel/ContactCarousel";

const pageQuery = groq`
*[_type == "contactPage"][0...10] | order(orderRank) {
   _id,
  seo {
    "metaTitle": coalesce(metaTitle[$locale], metaTitle[$defaultLocale]),
    "metaDesc": coalesce(metaDesc[$locale], metaDesc[$defaultLocale]),
    "shareTitle": coalesce(shareTitle[$locale], shareTitle[$defaultLocale]),
    "shareDesc": coalesce(shareDesc[$locale], shareDesc[$defaultLocale]),
    shareGraphic {
      asset->{
        ...,
        "alt": coalesce(alt[$locale], alt[$defaultLocale])
      }
    }
  },
  "slug": slug.current,
  form {
    _key,
    "heading": coalesce(heading[$locale], heading[$defaultLocale]),
    "shortText": coalesce(shortText[$locale], shortText[$defaultLocale]),
    "buttonText": coalesce(buttonText[$locale], buttonText[$defaultLocale]),
    sendToCRM,
    templateId,
    sendTo,
    "validErrorText": coalesce(validErrorText[$locale], validErrorText[$defaultLocale]),
    fields[] {
      ...,
      "label": coalesce(label[$locale], label[$defaultLocale]),
      "placeholder": coalesce(placeholder[$locale], placeholder[$defaultLocale]),
      "inputValidErrorText": coalesce(inputValidErrorText[$locale], inputValidErrorText[$defaultLocale]),
      items[]{
        ...,
        "value": coalesce(value[$locale], value[$defaultLocale]),
        "label": coalesce(value[$locale], value[$defaultLocale]),
      }
    }
  }
}
`;

const locationsGroq = groq`
  *[_type == "locations"][0]{
      locations[]{
        ...,
        "variant": "location",
        "address": coalesce(address[$locale], address[$defaultLocale]),
        "title": coalesce(title[$locale], title[$defaultLocale]),
        "subtitle": coalesce(subtitle[$locale], subtitle[$defaultLocale]),
        image {
          asset->{
             ...,
             "alt": coalesce(alt[$locale], alt[$defaultLocale])
          }
        },
        "link": link[0] {
          "title": coalesce(title[$locale], title[$defaultLocale]),
          _type == "externalLink" => {
            url,
            blank
          },
          _type == "internalLink" => {
            page->{ _type, "slug": slug.current },
            params,
          },
        }
      }
  }
`;

const formFieldSchema = z
  .object({
    label: z.string().nullable(),
    placeholder: z.string().nullable(),
    inputValidErrorText: z.string().nullable(),
    items: z
      .array(
        z
          .object({
            value: z.string(),
            label: z.string(),
          })
          .passthrough()
      )
      .nullable(),
  })
  .passthrough();

const pageQuerySchema = z.array(
  z.object({
    _id: z.string(),
    slug: z.string(),
    seo: seoQuerySchema,
    form: z.object({
      heading: z.string().nullable(),
      shortText: z.string().nullable(),
      buttonText: z.string().nullable(),
      templateId: z.string().nullable(),
      sendToCRM: z.boolean().nullable(),
      sendTo: z.string().nullable(),
      validErrorText: z.string().nullable(),
      fields: z.array(formFieldSchema).nullable(),
    }),
  })
);

type PageQuery = z.infer<typeof pageQuerySchema>;

const fetcher = async (
  query: string,
  params: { locale: string; defaultLocale: string }
) => {
  const res = await sanityClient.fetch(query, params);
  return pageQuerySchema.parse(res);
};

const LocationFetcher = async (
  query: string,
  params: { locale: string; defaultLocale: string }
) => {
  const res = await sanityClient.fetch(query, params);
  return res;
};

type Props = {
  settings: any;
  locations: LocationsType;
  layout: MainLayoutQuery;
  page: PageQuery;
  query: string;
  queryParams: {
    locale: string;
    defaultLocale: string;
  };
};

export const getStaticProps: GetStaticProps<Props> = async ({
  locale = "it",
  defaultLocale = "it",
}) => {
  const queryParams = {
    locale,
    defaultLocale,
  };
  const query = pageQuery;
  const [layout, settings, page, locations] = await Promise.all([
    getMainLayoutQuery(queryParams),
    sanityClient.fetch(settingsQuery, queryParams),
    fetcher(pageQuery, queryParams),
    LocationFetcher(locationsGroq, queryParams),
  ]);
  return {
    props: {
      layout,
      settings,
      page,
      query,
      queryParams,
      locations,
    },
    revalidate: 60,
  };
};

const ContactsPage: NextPage<Props> = (props) => {
  const { queryParams, query, settings, locations } = props;
  const { data } = useSWR([query, queryParams], fetcher, {
    fallbackData: props.page,
  });

  // Merge page seo with general seo
  const seo = useMemo(() => {
    // get seo of first form
    let formSeo 
    if (data) {
      if (typeof data[0]?.seo !== "object") return {};
      formSeo = data[0]?.seo
    }
    if (typeof settings?.seo !== "object") return {};
    return mergeWith(settings.seo, formSeo, (a, b) => b === null ? a : undefined);
  }, [data, settings?.seo]);


  return (
    <>
      {seo && <SEO {...seo} />}
      {!!data && <Contacts pages={data as any /* TODO: express types */} />}
      {locations?.locations && (
        <ContactCarousel locations={locations.locations} />
      )}
      <GridOutline />
    </>
  );
};

export default ContactsPage;
